import { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Search, Mail, MessageSquare, CheckCircle } from 'lucide-react';

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'supportTickets'),
        orderBy('createdAt', 'desc'),
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setTickets(items);
    } catch (error) {
      console.error('Error fetching support tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTicket = ticket => {
    setSelectedTicket(ticket);
    setReply(ticket.adminReply || '');
  };

  const handleReply = async () => {
    if (!selectedTicket) return;
    if (!reply.trim()) return;

    try {
      setSendingReply(true);
      const update = {
        adminReply: reply.trim(),
        status: 'answered',
        answeredAt: serverTimestamp(),
      };

      // Update root ticket (used by admin list)
      const ticketRef = doc(db, 'supportTickets', selectedTicket.id);
      await updateDoc(ticketRef, update);

      // Also mirror update into user's support subcollection (used by mobile)
      if (selectedTicket.userId) {
        try {
          const userTicketRef = doc(
            db,
            'users',
            selectedTicket.userId,
            'supportTickets',
            selectedTicket.id,
          );
          await updateDoc(userTicketRef, update);
        } catch (err) {
          console.warn('Failed to update user support ticket mirror:', err);
        }
      }

      setTickets(prev =>
        prev.map(t =>
          t.id === selectedTicket.id
            ? { ...t, adminReply: reply.trim(), status: 'answered' }
            : t,
        ),
      );

      setSelectedTicket(prev =>
        prev ? { ...prev, adminReply: reply.trim(), status: 'answered' } : prev,
      );
    } catch (error) {
      console.error('Error sending support reply:', error);
    } finally {
      setSendingReply(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      (ticket.subject || '').toLowerCase().includes(term) ||
      (ticket.userEmail || '').toLowerCase().includes(term) ||
      (ticket.message || '').toLowerCase().includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-yellow-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="text-yellow-500" />
            Support
          </h1>
        </div>

        <div className="mb-4 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by subject, email, or message..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
            />
          </div>
          <button
            onClick={fetchTickets}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black text-yellow-300 text-sm font-semibold hover:bg-gray-900"
          >
            <CheckCircle className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-white rounded-2xl shadow p-4 border border-gray-100 max-h-[70vh] overflow-y-auto">
            {loading ? (
              <p className="text-sm text-gray-500">Loading tickets...</p>
            ) : filteredTickets.length === 0 ? (
              <p className="text-sm text-gray-500">
                No support tickets found.
              </p>
            ) : (
              <ul className="space-y-2">
                {filteredTickets.map(ticket => (
                  <li key={ticket.id}>
                    <button
                      onClick={() => handleSelectTicket(ticket)}
                      className={`w-full text-left p-3 rounded-xl border text-sm ${
                        selectedTicket?.id === ticket.id
                          ? 'border-yellow-400 bg-yellow-50'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-gray-900 truncate">
                          {ticket.subject || 'No subject'}
                        </p>
                        <span
                          className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            ticket.status === 'answered'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {ticket.status || 'open'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate mb-1">
                        {ticket.userEmail || 'Unknown user'}
                      </p>
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {ticket.message}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl shadow p-6 border border-gray-100">
            {selectedTicket ? (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.2em] mb-1">
                    Ticket details
                  </p>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">
                    {selectedTicket.subject || 'No subject'}
                  </h2>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="h-4 w-4" />
                    <span>{selectedTicket.userEmail || 'Unknown user'}</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-800 whitespace-pre-line">
                  {selectedTicket.message}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admin reply
                  </label>
                  <textarea
                    rows={5}
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder="Write your reply to the user..."
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={handleReply}
                      disabled={sendingReply || !reply.trim()}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black text-yellow-300 text-sm font-semibold hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <MessageSquare className="h-4 w-4" />
                      {sendingReply ? 'Sending...' : 'Send reply'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-gray-500">
                Select a ticket from the list to view and reply.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;


